import React, { useEffect, useState } from "react";
import { LuBookOpen, LuCheck, LuChevronDown, LuDownload, LuInfo, LuRefreshCw, LuTriangleAlert } from "react-icons/lu";
import { Badge, Button, Dialog, EmptyState, Spinner, cn } from "../../components/ui";
import { plural, t } from "../../i18n";
import { ptBR } from "../../i18n/pt-BR";
import { useSession } from "../../context/SessionContext";
import { FundTecItem, ItemRecurso, ReferenciaSelecionadaPayload } from "../../types/resources.types";
import { procedimentosToFundTecItems, opmesToFundTecItems } from "../../lib/mappers";
import { ReferenceItem } from "./ReferenceItem";
import { referenceUrl, useReferences } from "./useReferences";

type Step = "prompt" | "loading" | "validacao";
const MESSAGES = ptBR.references.review.loading;

interface ReferencesReviewDialogProps {
  onAprove: (referencias: ReferenciaSelecionadaPayload[]) => void;
  onSkip: () => void;
  onCancel: () => void;
  itensSelecionados: ItemRecurso[];
  descricaoPaciente: string;
  state: ReturnType<typeof useReferences>;
  initialStep?: "prompt" | "validacao";
}

/** Pergunta se inclui referências no resumo, busca e deixa o médico revisar antes de "Confirmar e baixar". */
export const ReferencesReviewDialog: React.FC<ReferencesReviewDialogProps> = ({
  onAprove,
  onSkip,
  onCancel,
  itensSelecionados,
  descricaoPaciente,
  state,
  initialStep = "prompt",
}) => {
  const [step, setStep] = useState<Step>(initialStep);
  const [msgIndex, setMsgIndex] = useState(0);
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());
  const { setMetadata } = useSession();

  const procItems = procedimentosToFundTecItems(state.procedimentos);
  const opmeItems = opmesToFundTecItems(state.opmes);
  const firstItemId = procItems[0]?.id ?? opmeItems[0]?.id;

  useEffect(() => {
    if (step !== "loading") return;
    const id = setInterval(() => setMsgIndex((i) => (i + 1) % MESSAGES.length), 2800);
    return () => clearInterval(id);
  }, [step]);

  // O primeiro item já vem aberto, como no protótipo.
  useEffect(() => {
    if (firstItemId) setOpenItems((prev) => (prev.size ? prev : new Set([firstItemId])));
  }, [firstItemId]);

  const search = async () => {
    setMsgIndex(0);
    setStep("loading");
    await state.reloadReferencias(descricaoPaciente, itensSelecionados);
    setStep("validacao");
    setMetadata((prev) => ({ ...prev, total_resumo_com_referencias: prev.total_resumo_com_referencias + 1 }));
  };

  const confirm = () =>
    onAprove(
      state.selectedReferenciasFlat.map((r) => ({
        id: r.id,
        procedimento_alvo: r.procedimento_alvo ?? "",
        opme_alvo: r.opme_alvo ?? "",
        tipo: r.tipo,
        autores: r.autores,
        ano: r.ano,
        titulo: r.titulo,
        fonte: r.fonte,
        url: r.url,
        doi: r.doi,
        base: r.base,
        citacao_abnt: r.citacao_abnt,
      }))
    );

  const toggleItem = (id: string) =>
    setOpenItems((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  if (step === "prompt") {
    return (
      <Dialog
        open
        onClose={onCancel}
        size="sm"
        title={t("references.review.promptTitle")}
        footer={
          <div className="flex w-full flex-col gap-2">
            <Button variant="primary" fullWidth icon={<LuCheck size={18} aria-hidden />} onClick={search}>
              {t("references.review.yes")}
            </Button>
            <Button variant="outline" fullWidth onClick={onSkip}>
              {t("references.review.no")}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col items-center gap-3 text-center">
          <span className="grid h-14 w-14 place-items-center rounded-panel bg-primary-soft text-primary" aria-hidden>
            <LuBookOpen size={28} />
          </span>
          <p className="text-body text-fg-muted">{t("references.review.promptBody")}</p>
        </div>
      </Dialog>
    );
  }

  const failed = step === "validacao" && !!state.errorRefs && !state.hasRefs;

  if (step === "loading" || failed) {
    return (
      <Dialog
        open
        onClose={onCancel}
        size="sm"
        title={t("references.review.title")}
        footer={
          failed && (
            <>
              <Button variant="outline" onClick={onSkip}>
                {t("references.review.skipDownload")}
              </Button>
              <Button variant="primary" icon={<LuRefreshCw size={18} aria-hidden />} onClick={search}>
                {t("common.retry")}
              </Button>
            </>
          )
        }
      >
        {failed ? (
          <div className="flex items-start gap-3 rounded-card border border-danger bg-danger-soft p-4 text-body text-fg">
            <LuTriangleAlert size={20} className="mt-0.5 shrink-0 text-danger" aria-hidden />
            <p>
              <strong>{t("references.review.errorTitle")}</strong> {state.errorRefs}
            </p>
          </div>
        ) : (
          <div role="status" className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="text-primary">
              <Spinner size={48} />
            </span>
            <p key={msgIndex} className="animate-fade-in font-semibold text-fg">
              {MESSAGES[msgIndex]}
            </p>
            <p className="text-caption text-fg-subtle">{t("references.review.loadingHint")}</p>
            <span className="flex gap-1.5" aria-hidden>
              {MESSAGES.map((_, i) => (
                <i key={i} className={cn("h-1.5 w-1.5 rounded-full", i === msgIndex ? "bg-primary" : "bg-line")} />
              ))}
            </span>
            <Button variant="text" size="sm" onClick={onCancel}>
              {t("common.cancel")}
            </Button>
          </div>
        )}
      </Dialog>
    );
  }

  const total = state.selectedReferenciasFlat.length;
  const hasSelected = total > 0;
  const linksOk = hasSelected && state.openedCount === state.linkCount;

  const section = (title: string, items: FundTecItem[], kind: "CODIGO" | "MATERIAL", loading: boolean) => {
    if (items.length === 0) return null;
    const refs = items.flatMap((x) => x.referencias);
    return (
      <section className="overflow-hidden rounded-card border border-line">
        <div className="flex items-center justify-between gap-2 border-b border-line bg-surface-muted px-3.5 py-3">
          <strong className="flex items-center gap-2 text-body font-semibold">
            <span className={cn("h-2 w-2 rounded-full", kind === "CODIGO" ? "bg-primary" : "bg-fg-subtle")} aria-hidden />
            {title}
            <Badge tone="primary">
              {refs.filter((r) => state.selectedRefs[r.id]).length}/{refs.length}
            </Badge>
          </strong>
          <Button
            variant="outline"
            size="sm"
            loading={loading}
            disabled={state.loadingProc || state.loadingOpme}
            icon={<LuRefreshCw size={16} aria-hidden />}
            onClick={() => state.reloadReferencias(descricaoPaciente, itensSelecionados, kind)}
          >
            {t(loading ? "references.review.reloading" : "references.review.reload")}
          </Button>
        </div>
        <div className="flex flex-col gap-2.5 p-3">
          {items.map((item) => {
            const open = openItems.has(item.id);
            const selected = item.referencias.filter((r) => state.selectedRefs[r.id]).length;
            return (
              <div key={item.id} className="rounded-control border border-line bg-surface">
                <button
                  type="button"
                  aria-expanded={open}
                  onClick={() => toggleItem(item.id)}
                  className="flex w-full items-start gap-2.5 p-3.5 text-left focus-visible:outline-none focus-visible:shadow-focus"
                >
                  <span className="flex min-w-0 flex-1 flex-col gap-1">
                    <strong className="break-words text-body font-semibold">{item.nome}</strong>
                    {item.justificativa && <span className="text-caption text-fg-subtle">{item.justificativa}</span>}
                    <span>
                      <Badge tone={selected ? "primary" : "neutral"}>
                        {t("references.review.itemSelected", { selected, total: item.referencias.length })}
                      </Badge>
                    </span>
                  </span>
                  <LuChevronDown size={20} aria-hidden className={cn("mt-0.5 shrink-0 text-fg-subtle transition-transform duration-fast", open && "rotate-180")} />
                </button>
                {open && (
                  <div className="flex flex-col gap-2.5 px-3.5 pb-3.5">
                    {item.referencias.map((ref) => {
                      const url = referenceUrl(ref);
                      return (
                        <ReferenceItem
                          key={ref.id}
                          item={ref}
                          selected={!!state.selectedRefs[ref.id]}
                          opened={!!url && state.openedUrls.has(url)}
                          onToggle={state.toggleSelectRef}
                          onOpen={state.markRefOpened}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>
    );
  };

  const check = (ok: boolean, label: string) => (
    <span className={cn("inline-flex items-center gap-1.5", ok ? "text-success" : "text-fg-muted")}>
      {ok ? <LuCheck size={15} aria-hidden /> : <LuInfo size={15} aria-hidden />}
      {label}
    </span>
  );

  return (
    <Dialog
      open
      onClose={onCancel}
      size="lg"
      title={
        <span className="flex items-center gap-2.5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-control bg-primary-soft text-primary" aria-hidden>
            <LuBookOpen size={20} />
          </span>
          {t("references.review.title")}
        </span>
      }
      description={state.hasRefs ? plural(total, ptBR.references.review.selected) : undefined}
      footer={
        state.hasRefs && (
          <div className="flex w-full flex-col gap-3">
            <div className="flex flex-wrap gap-x-3.5 gap-y-1.5 text-caption" aria-live="polite">
              {check(hasSelected, t("references.review.atLeastOne"))}
              {check(linksOk, t("references.review.linksChecked", { opened: state.openedCount, total: state.linkCount }))}
            </div>
            <label className="flex cursor-pointer items-start gap-2.5 rounded-control border border-primary-line bg-primary-tint px-3 py-2.5 text-caption text-fg-secondary">
              <input
                type="checkbox"
                checked={state.declared}
                onChange={state.toggleDeclaracao}
                className="mt-0.5 h-5 w-5 shrink-0 accent-[rgb(var(--primary))]"
              />
              {t("references.review.declaration")}
            </label>
            <div className="flex gap-2.5 sm:justify-end">
              <Button variant="outline" className="hidden sm:inline-flex" onClick={onCancel}>
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                className="flex-1 sm:flex-none"
                disabled={!state.canConfirm}
                icon={<LuDownload size={18} aria-hidden />}
                onClick={confirm}
              >
                {t("references.review.confirm")}
              </Button>
            </div>
          </div>
        )
      }
    >
      {state.hasRefs ? (
        <div className="flex flex-col gap-4">
          <p className="text-caption text-fg-muted">{t("references.review.intro")}</p>
          {state.errorRefs && (
            <p className="flex items-start gap-2 rounded-control border border-danger bg-danger-soft p-3 text-caption text-fg">
              <LuTriangleAlert size={16} className="mt-0.5 shrink-0 text-danger" aria-hidden />
              {state.errorRefs}
            </p>
          )}
          {section(t("references.review.procedures"), procItems, "CODIGO", state.loadingProc)}
          {section(t("references.review.opme"), opmeItems, "MATERIAL", state.loadingOpme)}
        </div>
      ) : (
        <EmptyState
          title={t("references.review.emptyTitle")}
          description={t("references.review.emptyBody")}
          action={
            <Button variant="primary" onClick={onSkip}>
              {t("references.review.continueWithout")}
            </Button>
          }
        />
      )}
    </Dialog>
  );
};
